/**
 * =============================================================================
 * PayoutCommissionsModal — Drill-down de comissões de um Pagamento (Indicação)
 *
 * Por que existe:
 *   Um pagamento (`affiliate_payouts`) pode agregar N comissões
 *   (`affiliate_commissions`), e cada comissão tem seu próprio negócio,
 *   cliente e indicação. A listagem de pagamentos não pode achatar essa
 *   relação 1:N — fazer isso esconde dados e quebra a leitura quando o
 *   pagamento agrega 2+ comissões.
 *
 *   Este modal é o ponto único onde cada comissão vinculada ao pagamento
 *   é mostrada em uma linha própria, no MESMO padrão visual (StatusBar,
 *   table-fixed, colgroup, header sticky) da `CommissionsTableView` e da
 *   `PayoutsTableView`.
 *
 * Acesso:
 *   - Aberto a partir do botão pill "N comissões →" da `PayoutsTableView`.
 *   - Reusa a mesma RPC `get_referral_payout_details` consumida pelo
 *     `PayoutDetailSheet` (TanStack Query dedupa quando ambos abertos).
 *
 * @version 1.0.0
 * RELEASE:
 *   2026-04-30 v1.0.0 - Criação. Substitui as colunas de Negócio/Cliente/
 *                       Indicação que existiam por linha de pagamento.
 * =============================================================================
 */
import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Wallet,
  Briefcase,
  ExternalLink,
  Hash,
  CircleDollarSign,
  HandCoins,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { StatusBar } from '@/components/referral/shared/StatusBar';
import {
  useReferralPayoutDetails,
  type PayoutDetailsCommission,
  type PayoutStatus,
} from '@/hooks/useReferralPayoutDetails';

// ============================================================================
// CONSTANTES (paridade visual com PayoutsTableView)
// ============================================================================

/** Mapeamento de status do pagamento → label e cor do badge no header. */
const STATUS_CONFIG: Record<PayoutStatus, { label: string; color: string }> = {
  pending:    { label: 'Pendente',    color: 'bg-yellow-500 text-white border-yellow-500' },
  processing: { label: 'Processando', color: 'bg-blue-500 text-white border-blue-500' },
  paid:       { label: 'Pago',        color: 'bg-green-500 text-white border-green-500' },
  failed:     { label: 'Falhou',      color: 'bg-red-500 text-white border-red-500' },
  cancelled:  { label: 'Cancelado',   color: 'bg-muted-foreground text-white border-muted-foreground' },
};

// ============================================================================
// UTILITÁRIOS (cópia local para isolar o componente)
// ============================================================================

/** Formata valor monetário em BRL. */
function formatBRL(value: number | null | undefined): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value ?? 0));
}

/** Formata CPF / CNPJ (máscara padrão brasileira). */
function formatCpfCnpj(
  cpf?: string | null,
  cnpj?: string | null,
): string | null {
  const raw = (cnpj || cpf || '').replace(/\D/g, '');
  if (!raw) return null;
  if (raw.length === 11) {
    return raw.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (raw.length === 14) {
    return raw.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return cnpj || cpf || null;
}

// ============================================================================
// LINHA DA TABELA INTERNA
// ============================================================================

function CommissionRow({
  commission,
  onOpenDeal,
  onOpenCustomer,
}: {
  commission: PayoutDetailsCommission;
  onOpenDeal?: (dealId: string) => void;
  onOpenCustomer?: (customerId: string) => void;
}) {
  const customerDoc = formatCpfCnpj(commission.customer_cpf, commission.customer_cnpj);
  const customerName = commission.customer_name || commission.lead_name || null;
  const dealRemoved = !!commission.deal_id && !commission.deal_title;
  const dealClickable = !!onOpenDeal && !!commission.deal_id && !dealRemoved;

  return (
    <TableRow className="hover:bg-muted/40 border-b border-border/40 transition-colors">
      {/* Barra lateral neutra (status individual de comissão fica fora deste modal). */}
      <TableCell className="p-0 w-2">
        <StatusBar colorClass="bg-muted-foreground/30" />
      </TableCell>

      {/* Ref. da comissão (8 chars uppercase, sem expor UUID completo). */}
      <TableCell className="px-2 py-1.5">
        <span className="font-mono text-[11px] text-muted-foreground">
          {commission.id.substring(0, 8).toUpperCase()}
        </span>
      </TableCell>

      {/* Nº Negócio — pill clicável (abre DealDetailsModalPremium). */}
      <TableCell className="px-2 py-1.5">
        {commission.deal_id ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (dealClickable && commission.deal_id) onOpenDeal!(commission.deal_id);
            }}
            disabled={!dealClickable}
            className={cn(
              'inline-flex items-center gap-1 font-mono text-[11px] uppercase',
              dealClickable
                ? 'text-primary hover:underline cursor-pointer'
                : 'text-muted-foreground cursor-default',
            )}
            title={dealRemoved ? 'Negócio removido' : commission.deal_title || undefined}
          >
            <Briefcase className="h-3 w-3 shrink-0" />
            {commission.deal_id.substring(0, 8).toUpperCase()}
            {dealClickable && <ExternalLink className="h-2.5 w-2.5 opacity-60" />}
          </button>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>

      {/* Cliente — Nome + CPF/CNPJ (clicável → CustomerDetailsModalPremium).
          Paridade visual com a coluna Nº Negócio: quando clicável, aplica
          cor `primary`, sublinhado no hover e ícone ExternalLink (↗) ao lado
          do nome — sinal claro de que abre o modal do cliente. */}
      <TableCell className="px-2 py-1.5 max-w-[200px]">
        {commission.customer_id || customerName ? (
          (() => {
            const customerClickable = !!onOpenCustomer && !!commission.customer_id;
            return (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (customerClickable) {
                    onOpenCustomer!(commission.customer_id!);
                  }
                }}
                disabled={!customerClickable}
                className={cn(
                  'flex flex-col items-start min-w-0 w-full text-left',
                  customerClickable ? 'cursor-pointer' : 'cursor-default',
                )}
                title={customerClickable ? 'Abrir cliente' : undefined}
              >
                <span
                  className={cn(
                    'inline-flex items-center gap-1 font-medium text-xs max-w-full',
                    customerClickable
                      ? 'text-primary hover:underline'
                      : 'text-foreground',
                  )}
                >
                  <span className="truncate">{customerName ?? '—'}</span>
                  {customerClickable && (
                    <ExternalLink className="h-2.5 w-2.5 opacity-60 shrink-0" />
                  )}
                </span>
                {customerDoc && (
                  <span className="text-[10px] text-muted-foreground tabular-nums truncate max-w-full">
                    {customerDoc}
                  </span>
                )}
              </button>
            );
          })()
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>

      {/* Lead (texto cru da indicação original). */}
      <TableCell className="px-2 py-1.5 text-xs text-muted-foreground max-w-[160px]">
        <span className="truncate block">{commission.lead_name ?? '—'}</span>
      </TableCell>

      {/* Nº Indicação — UUID curto (sem tooltip, regra v1.2.0 do projeto). */}
      <TableCell className="px-2 py-1.5">
        {commission.referral_id ? (
          <span className="inline-flex items-center gap-1 font-mono text-[11px] uppercase text-muted-foreground">
            <Hash className="h-3 w-3 shrink-0" />
            {commission.referral_id.substring(0, 8).toUpperCase()}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>

      {/* Valor da comissão. */}
      <TableCell className="text-right px-2 py-1.5">
        <span className="text-sm font-medium tabular-nums">
          {formatBRL(commission.reward_value)}
        </span>
      </TableCell>

      {/* Criada em. */}
      <TableCell className="text-xs text-muted-foreground px-2 py-1.5">
        {format(new Date(commission.created_at), 'dd/MM/yy', { locale: ptBR })}
      </TableCell>
    </TableRow>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

interface PayoutCommissionsModalProps {
  /** ID do pagamento — `null` mantém o modal fechado. */
  payoutId: string | null;
  /** Estado controlado de abertura (espelha `!!payoutId`). */
  open: boolean;
  /** Callback de fechamento (limpa o `payoutId` na página). */
  onClose: () => void;
  /** Abre o modal premium do negócio vinculado à comissão. */
  onOpenDeal?: (dealId: string) => void;
  /** Abre o modal de detalhes do cliente vinculado à comissão. */
  onOpenCustomer?: (customerId: string) => void;
}

/**
 * Modal de drill-down: lista todas as comissões de um pagamento.
 *
 * Usa o mesmo hook `useReferralPayoutDetails` consumido pelo
 * `PayoutDetailSheet` — TanStack Query dedupa o fetch quando ambos
 * estiverem abertos para o mesmo pagamento.
 */
export function PayoutCommissionsModal({
  payoutId,
  open,
  onClose,
  onOpenDeal,
  onOpenCustomer,
}: PayoutCommissionsModalProps) {
  // Hook reaproveitado (realtime já incluído).
  const { data, isLoading } = useReferralPayoutDetails(payoutId, open);

  const payout = data?.payout;
  const affiliate = data?.affiliate;
  const commissions = data?.commissions ?? [];

  const statusInfo = payout
    ? STATUS_CONFIG[payout.status] ?? STATUS_CONFIG.pending
    : null;

  // Soma client-side dos valores das comissões (auditoria visual no footer).
  const total = useMemo(
    () => commissions.reduce((sum, c) => sum + Number(c.reward_value ?? 0), 0),
    [commissions],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      {/* Layout "wizard premium" do projeto: max-w-5xl + 85dvh + p-0,
          fullscreen no mobile. */}
      <DialogContent className="max-w-5xl w-[95vw] max-h-[85dvh] p-0 gap-0 flex flex-col">
        {/* ====================== HEADER ====================== */}
        <DialogHeader className="px-5 py-4 border-b border-border/60 space-y-1.5 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Wallet className="h-3.5 w-3.5" />
            </span>
            Comissões do pagamento
            {payoutId && (
              <span className="font-mono text-xs text-muted-foreground uppercase">
                #{payoutId.substring(0, 8).toUpperCase()}
              </span>
            )}
          </DialogTitle>
          {payout && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="truncate max-w-[260px]">
                {affiliate?.full_name ?? 'Afiliado removido'}
              </span>
              <span>•</span>
              <span className="font-medium tabular-nums text-foreground">
                {formatBRL(payout.amount)}
              </span>
              <span>•</span>
              {statusInfo && (
                <Badge
                  variant="outline"
                  className={cn('text-[10px] h-5', statusInfo.color)}
                >
                  {statusInfo.label}
                </Badge>
              )}
            </div>
          )}
        </DialogHeader>

        {/* ====================== CORPO ====================== */}
        <div className="flex-1 min-h-0 overflow-auto">
          {isLoading ? (
            // Skeleton em altura compatível com a tabela.
            <div className="space-y-2 p-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : commissions.length === 0 ? (
            // Estado vazio (não esperado, mas blindado).
            <div className="flex flex-col items-center justify-center h-64 gap-3">
              <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center">
                <HandCoins className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="text-center">
                <h3 className="font-medium text-sm">Nenhuma comissão vinculada</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Este pagamento não possui comissões associadas.
                </p>
              </div>
            </div>
          ) : (
            // Tabela full-bleed com largura mínima — paridade visual.
            <table
              className="w-max caption-bottom text-sm table-fixed"
              style={{ minWidth: '900px' }}
            >
              <colgroup>
                <col style={{ width: 8 }} />   {/* Barra de status */}
                <col className="w-[80px]" />   {/* Ref. */}
                <col className="w-[110px]" />  {/* Nº Negócio */}
                <col className="w-[200px]" />  {/* Cliente */}
                <col className="w-[160px]" />  {/* Lead */}
                <col className="w-[110px]" />  {/* Nº Indicação */}
                <col className="w-[120px]" />  {/* Valor */}
                <col className="w-[90px]" />   {/* Criada em */}
              </colgroup>

              <TableHeader className="sticky top-0 bg-background z-[1]">
                <TableRow className="bg-muted/20 hover:bg-muted/20 border-b border-border/40">
                  <TableHead className="w-2 p-0" />
                  <TableHead className="text-xs font-medium px-2 py-2">Ref.</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1.5">Nº Negócio</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1.5">Cliente</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1.5">Lead</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1.5">Nº Indicação</TableHead>
                  <TableHead className="text-xs font-medium text-right px-2 py-1.5">Valor</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1.5">Criada em</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {commissions.map((c) => (
                  <CommissionRow
                    key={c.id}
                    commission={c}
                    onOpenDeal={onOpenDeal}
                    onOpenCustomer={onOpenCustomer}
                  />
                ))}
              </TableBody>
            </table>
          )}
        </div>

        {/* ====================== FOOTER ====================== */}
        <div className="px-5 py-3 border-t border-border/60 flex items-center justify-between gap-3 shrink-0 bg-muted/10">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <CircleDollarSign className="h-3.5 w-3.5" />
            <span>
              <span className="font-medium text-foreground tabular-nums">
                {commissions.length}
              </span>{' '}
              {commissions.length === 1 ? 'comissão' : 'comissões'} • Total:{' '}
              <span className="font-medium text-foreground tabular-nums">
                {formatBRL(total)}
              </span>
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
